import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

/**
 * Settings Page
 * User settings and preferences page.
 *
 * @author Civilhealth Development Team
 */
const SettingsPage: React.FC = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    appointmentReminders: true,
    marketingEmails: false,
    twoFactorAuth: false,
    autoLogout: true,
    darkMode: false,
    language: 'en',
    timezone: 'Asia/Kolkata',
  });

  const handleToggle = (key: string) => {
    setSettings({ ...settings, [key]: !settings[key as keyof typeof settings] });
  };

  const handleSave = () => {
    // TODO: Implement API call
    setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
  };

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Customize your application preferences
          </Typography>
        </Box>

        {/* Notifications Settings */}
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Notifications
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive notifications via email"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.emailNotifications}
                    onChange={() => handleToggle('emailNotifications')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="SMS Notifications"
                  secondary="Receive notifications via SMS"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.smsNotifications}
                    onChange={() => handleToggle('smsNotifications')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Appointment Reminders"
                  secondary="Get reminders before scheduled appointments"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.appointmentReminders}
                    onChange={() => handleToggle('appointmentReminders')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Marketing Emails"
                  secondary="Receive updates about new features and services"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.marketingEmails}
                    onChange={() => handleToggle('marketingEmails')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Security
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary="Add an extra layer of security to your account"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.twoFactorAuth}
                    onChange={() => handleToggle('twoFactorAuth')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Auto Logout"
                  secondary="Automatically logout after 30 minutes of inactivity"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.autoLogout}
                    onChange={() => handleToggle('autoLogout')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Appearance Settings */}
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Appearance
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Dark Mode"
                  secondary="Use dark theme for the application"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.darkMode}
                    onChange={() => handleToggle('darkMode')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Language"
                  secondary="Select your preferred language"
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2">English</Typography>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Timezone"
                  secondary="Set your local timezone"
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2">Asia/Kolkata (IST)</Typography>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ borderRadius: 2 }}
          >
            Save Settings
          </Button>
        </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;